import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Square, Bed, Bath, Heart } from 'lucide-react';
import { useFavorites } from '../hooks/useFavorites';

const PropertyCard = ({ property, hideFavorite = false }) => {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      apartment: 'Appartement',
      house: 'Maison',
      studio: 'Studio',
      loft: 'Loft',
      villa: 'Villa',
    };
    return labels[type] || type;
  };

  const getTransactionLabel = (type) => {
    return type === 'sale' ? 'Vente' : 'Location';
  };

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer group">
      <div className="relative" onClick={() => navigate(`/properties/${property.id}`)}>
        {/* Image */}
        <div className="relative h-56 overflow-hidden bg-gray-200">
          {property.images && property.images.length > 0 ? (
            <img
              src={property.images[0]}
              alt={property.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <Square className="h-16 w-16" />
            </div>
          )}
          
          {/* Badges */}
          <div className="absolute top-3 left-3">
            <Badge
              className="bg-[#FF9F68] text-white hover:bg-[#E67E4F] font-semibold"
            >
              {getTransactionLabel(property.transaction_type)}
            </Badge>
          </div>
          
          {/* Favorite Button */}
          {!hideFavorite && (
            <Button
              size="icon"
              variant="ghost"
              className={`absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full transition-all duration-200 ${
                isFavorite(property.id) ? 'hover:scale-110' : ''
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await toggleFavorite(property.id);
                } catch (error) {
                  console.error('Error toggling favorite:', error);
                }
              }}
            >
              <Heart
                className={`h-5 w-5 transition-all duration-200 ${
                  isFavorite(property.id)
                    ? 'fill-red-500 text-red-500'
                    : 'text-gray-700'
                }`}
              />
            </Button>
          )}
        </div>
      </div>

      <CardContent className="p-4" onClick={() => navigate(`/properties/${property.id}`)}>
        {/* Price */}
        <div className="mb-2">
          <h3 className="text-2xl font-bold text-[#FF9F68]">
            {formatPrice(property.price)}
            {property.transaction_type === 'rent' && (
              <span className="text-lg text-gray-600">/mois</span>
            )}
          </h3>
        </div>

        {/* Title */}
        <h4 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
          {property.title}
        </h4>

        {/* Location */}
        <div className="flex items-center text-gray-600 mb-3">
          <MapPin className="h-4 w-4 mr-1" />
          <span className="text-sm">
            {property.city}, {property.postal_code}
          </span>
        </div>

        {/* Property Details */}
        <div className="flex items-center justify-between text-sm text-gray-600 pt-3 border-t border-gray-200">
          <div className="flex items-center">
            <Square className="h-4 w-4 mr-1" />
            <span>{property.surfaceArea || property.surface_area || '-'} m²</span>
          </div>
          <div className="flex items-center">
            <Bed className="h-4 w-4 mr-1" />
            <span>{property.bedrooms} ch.</span>
          </div>
          <div className="flex items-center">
            <Bath className="h-4 w-4 mr-1" />
            <span>{property.bathrooms} sdb</span>
          </div>
        </div>

        {/* Property Type */}
        <div className="mt-3">
          <Badge variant="secondary" className="text-xs">
            {getPropertyTypeLabel(property.property_type)}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;